import { interpolateColor } from '../charts/shared/palettes';

const DEFAULT_DARK_TEXT = '#111111';
const DEFAULT_LIGHT_TEXT = '#FFFFFF';

function normalizeHex(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.trim();
  if (!clean.startsWith('#')) return null;
  if (clean.length === 4) {
    return `#${clean[1]}${clean[1]}${clean[2]}${clean[2]}${clean[3]}${clean[3]}`.toUpperCase();
  }
  if (clean.length === 7) return clean.toUpperCase();
  return null;
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function channelToLinear(v) {
  const x = v / 255;
  return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(rgb) {
  if (!rgb) return null;
  const r = channelToLinear(rgb.r);
  const g = channelToLinear(rgb.g);
  const b = channelToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(foregroundHex, backgroundHex) {
  const fg = relativeLuminance(hexToRgb(foregroundHex));
  const bg = relativeLuminance(hexToRgb(backgroundHex));
  if (fg == null || bg == null) return 1;
  const lighter = Math.max(fg, bg);
  const darker = Math.min(fg, bg);
  return (lighter + 0.05) / (darker + 0.05);
}

export function getAccessibleTextColor({
  background,
  preferred = DEFAULT_DARK_TEXT,
  minRatio = 4.5,
  fallbackLight = DEFAULT_LIGHT_TEXT,
  fallbackDark = DEFAULT_DARK_TEXT,
}) {
  if (contrastRatio(preferred, background) >= minRatio) return preferred;
  const lightRatio = contrastRatio(fallbackLight, background);
  const darkRatio = contrastRatio(fallbackDark, background);
  return lightRatio >= darkRatio ? fallbackLight : fallbackDark;
}

export const SERIES_LINE_DASHARRAYS_SVG = ['0', '8 4', '4 3', '10 3 2 3'];
export const SERIES_LINE_STYLES_MPL = ['-', '--', ':', '-.'];
export const SERIES_MARKERS_SVG = ['circle', 'square', 'triangle', 'diamond', 'triangleDown', 'plus', 'cross', 'hexagon', 'pentagon', 'star'];
export const SERIES_MARKERS_MPL = ['o', 's', '^', 'D', 'v', 'P', 'X', 'h', 'p', '*'];
export const SERIES_HATCHES_MPL = ['/', '\\\\', 'x', 'o', '.'];

export function getSeriesDashSvg(index, useSeriesStyles) {
  if (!useSeriesStyles) return '0';
  return SERIES_LINE_DASHARRAYS_SVG[index % SERIES_LINE_DASHARRAYS_SVG.length];
}

export function getSeriesLineStyleMpl(index, useSeriesStyles) {
  if (!useSeriesStyles) return '-';
  return SERIES_LINE_STYLES_MPL[index % SERIES_LINE_STYLES_MPL.length];
}

export function getSeriesMarkerSvg(index, useSeriesStyles) {
  if (!useSeriesStyles) return 'circle';
  return SERIES_MARKERS_SVG[index % SERIES_MARKERS_SVG.length];
}

export function getSeriesMarkerMpl(index, useSeriesStyles) {
  if (!useSeriesStyles) return 'o';
  return SERIES_MARKERS_MPL[index % SERIES_MARKERS_MPL.length];
}

export function getSeriesHatchMpl(index, usePatternFills) {
  if (!usePatternFills) return '';
  return SERIES_HATCHES_MPL[index % SERIES_HATCHES_MPL.length];
}

/**
 * Returns true if the given hex color is perceptually dark (luminance < 0.4).
 */
export function isDark(hexColor) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return false;
  return relativeLuminance(rgb) < 0.4;
}

/**
 * Derive all chart-level theme colors from a background color.
 * Returns an overrides object suitable for setBulkOverrides().
 */
export function deriveThemeFromBackground(bgColor, baseTheme) {
  const bg = normalizeHex(bgColor);
  if (!bg) return {};

  const dark = isDark(bg);
  const textColor = getAccessibleTextColor({
    background: bg,
    preferred: dark ? '#F1F5F9' : '#111827',
  });
  // Grid: blend the text color toward the background at ~70% (subtle)
  const gridColor = interpolateColor(textColor, bg, 0.7);

  return {
    background: bg,
    plotBackground: bg,
    foreground: textColor,
    axisColor: textColor,
    tickColor: textColor,
    labelColor: textColor,
    titleColor: textColor,
    legendTextColor: textColor,
    gridColor,
    gridAlpha: dark ? 0.7 : 0.6,
  };
}

/**
 * Derive UI chrome colors from a background color.
 * Returns a ui overrides object.
 */
export function deriveUiFromBackground(bgColor) {
  const bg = normalizeHex(bgColor);
  if (!bg) return {};

  const dark = isDark(bg);
  const textColor = getAccessibleTextColor({
    background: bg,
    preferred: dark ? '#F1F5F9' : '#000000',
  });
  const textSecondary = getAccessibleTextColor({
    background: bg,
    preferred: dark ? '#94A3B8' : '#374151',
    minRatio: 3,
  });
  const borderColor = getAccessibleTextColor({
    background: bg,
    preferred: dark ? '#94A3B8' : '#000000',
    minRatio: 2,
  });
  const borderSecondary = dark
    ? interpolateColor(bg, '#FFFFFF', 0.2)
    : '#9CA3AF';
  const bgSecondary = dark
    ? interpolateColor(bg, '#FFFFFF', 0.06)
    : '#F9FAFB';
  const hover = dark
    ? interpolateColor(bg, '#FFFFFF', 0.1)
    : '#E5E7EB';
  const scrollTrack = dark
    ? interpolateColor(bg, '#FFFFFF', 0.08)
    : '#F0F0F0';
  const scrollThumb = dark
    ? interpolateColor(bg, '#FFFFFF', 0.3)
    : '#000000';

  return {
    bg,
    bgSecondary,
    text: textColor,
    textSecondary,
    border: borderColor,
    borderSecondary,
    hover,
    accent: textColor,
    accentFg: bg,
    scrollTrack,
    scrollThumb,
    overlayBackdrop: 'rgba(17, 24, 39, 0.35)',
    emptyStateText: textSecondary,
    codeKeyword: textColor,
    codeString: textSecondary,
    codeComment: dark
      ? interpolateColor(bg, '#FFFFFF', 0.35)
      : '#9CA3AF',
    codeNumber: textSecondary,
  };
}
